import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Printer, Refresh } from "iconoir-react";
import type { Puzzle, PuzzleSummary } from "../../shared/nonogram/types";
import { GRID_LINE_HEAVY_MM, GRID_LINE_MM, printBoardGeometry } from "../../shared/nonogram/printGeometry";
import {
  MAX_PRINT_COUNT,
  PRINT_SIZES,
  answerCellMm,
  isPrintSize,
  newPrintSeed,
  printCountOptions,
  printPageCount,
  printColumns,
  printPerPage,
  selectPrintable,
  type PrintSize,
} from "../../shared/nonogram/printSelection";
import type { PrintableSheet } from "../../shared/seo/printable";
import { seoPageForPath } from "../../shared/seo/pages";
import { SITE_DOMAIN, SITE_NAME } from "../../shared/seo/site";
import { QR_MODULES, QR_PATH } from "../../shared/seo/qrCode";
import { fetchPuzzleSummaries, fetchSolvedPuzzles } from "../lib/api";
import { SeoContent } from "../components/SeoContent";

type Options = {
  size: PrintSize;
  count: number;
  answers: boolean;
  /** Undefined means "the page as it is published": the one puzzle the static HTML
      already contains, so the first paint does not swap out from under the reader. */
  seed?: string;
};

/** The controls start on whatever the page publishes, so /printable-nonograms/15x15
    opens with 15×15 selected rather than snapping to it once the grids arrive. */
function readOptions(published: PrintableSheet): Options {
  const params = new URLSearchParams(window.location.search);
  const rawSize = Number(params.get("size"));
  const size: PrintSize = isPrintSize(rawSize) ? rawSize : published.size;
  const rawCount = Number(params.get("count"));
  const count =
    Number.isInteger(rawCount) && rawCount >= 1 && rawCount <= MAX_PRINT_COUNT ? rawCount : published.ids.length;
  return {
    size,
    count,
    answers: params.get("answers") !== "none",
    seed: params.get("seed") ?? undefined,
  };
}

function writeOptions(options: Options): void {
  const url = new URL(window.location.href);
  const preview = url.searchParams.get("preview");
  url.search = "";
  if (preview) url.searchParams.set("preview", preview);
  if (options.seed) {
    url.searchParams.set("size", String(options.size));
    url.searchParams.set("count", String(options.count));
    url.searchParams.set("seed", options.seed);
  }
  if (!options.answers) url.searchParams.set("answers", "none");
  window.history.replaceState(null, "", url);
}

/** One element per number. A column's clues read top to bottom and a row's read left
    to right, and neither can be a single string: "1 1 1 3" set in a 12mm-wide column
    soft-wraps into "1 1" over "1 3", which is a different puzzle. */
function Clue({ clues }: { clues: readonly number[] }) {
  return (
    <span>
      {(clues.length ? clues : [0]).map((clue, index) => <span key={index}>{clue}</span>)}
    </span>
  );
}

/** Kept in step with renderPrintBoard in shared/seo/render.ts: the same page has to
    print identically whether React drew it or the build did. */
function PrintBoard({ puzzle, answer, cellMm }: { puzzle: Puzzle; answer: boolean; cellMm?: number }) {
  const geometry = printBoardGeometry(puzzle, answer, cellMm);
  const side = `${puzzle.size}×${puzzle.size}`;

  return (
    <div
      className="print-board"
      data-size={puzzle.size}
      data-bare={cellMm ? "true" : undefined}
    >
      {!cellMm && (
        <>
          <div className="print-corner">{side}</div>
          <div className="print-column-clues">
            {puzzle.columnClues.map((clues, index) => <Clue clues={clues} key={index} />)}
          </div>
          <div className="print-row-clues">
            {puzzle.rowClues.map((clues, index) => <Clue clues={clues} key={index} />)}
          </div>
        </>
      )}
      <svg
        className="print-grid"
        viewBox={geometry.viewBox}
        width={`${geometry.widthMm}mm`}
        height={`${geometry.widthMm}mm`}
        role="img"
        aria-label={answer ? `The solved ${side} picture` : `An empty ${side} grid to fill in`}
      >
        {geometry.fills.map((fill) => (
          <rect key={`${fill.x}-${fill.y}`} x={fill.x} y={fill.y} width={geometry.cell} height={geometry.cell} />
        ))}
        {geometry.lines.map((line, index) => (
          <line
            key={index}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            strokeWidth={line.heavy ? GRID_LINE_HEAVY_MM : GRID_LINE_MM}
          />
        ))}
      </svg>
    </div>
  );
}

/** A paper sheet has no interface, so it says who made it and what the rules are. */
function PrintFootnote() {
  return (
    <p className="print-footnote">
      {SITE_DOMAIN} · Each number is one unbroken run of filled squares, in order, with a gap between runs.
    </p>
  );
}

/** Inline, not an <img>: a sheet coming out of a printer cannot wait for an image to
    load, and the path is machine-verified to decode back to the site's address. */
function PrintQr() {
  return (
    <figure className="print-qr">
      <svg viewBox={`0 0 ${QR_MODULES} ${QR_MODULES}`} role="img" aria-label={`QR code for ${SITE_DOMAIN}`}>
        <path d={QR_PATH} />
      </svg>
      <figcaption>Scan to play</figcaption>
    </figure>
  );
}

/** A sheet that leaves the browser has to say where it came from: the address is the
    only way back to the site from a piece of paper. */
function PrintBrand() {
  return (
    <p className="print-brand">
      <span>{SITE_NAME}</span>
      <span>{SITE_DOMAIN}</span>
    </p>
  );
}

/** A4 at the CSS-fixed 96dpi: 210mm of paper, in the pixels a sheet lays out at. */
const SHEET_WIDTH_PX = (210 * 96) / 25.4;

/**
 * On a phone the sheet is wider than the screen. It keeps its paper geometry — what is
 * on screen has to be what comes out of the printer — and gets scaled to fit instead,
 * the way a print dialog shows a page. The stylesheet has a fallback for the no-script
 * page; this makes it exact, and follows a rotation or a resize.
 */
function useSheetZoom(): React.RefObject<HTMLElement | null> {
  const page = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = page.current;
    if (!element) return undefined;
    function measure(): void {
      if (!element) return;
      const available = element.clientWidth;
      const zoom = available > 0 ? Math.min(1, available / SHEET_WIDTH_PX) : 1;
      element.style.setProperty("--sheet-zoom", String(Math.round(zoom * 1000) / 1000));
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    // Belt and braces: a rotation changes the viewport without necessarily changing
    // this element's box in a way every engine reports through the observer.
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  return page;
}

export function Printable({ path }: { path: string }) {
  // Every printable page publishes a fixed set of grids, and the build has already
  // put those grids in the HTML. React has to load the same ones or the sheet would
  // change under a reader who printed a moment too early.
  const seoPage = seoPageForPath(path);
  const published = seoPage?.printableSheet ?? { size: 5 as PrintSize, ids: [] };
  const [options, setOptions] = useState<Options>(() => readOptions(published));
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  // Nothing else can show what comes out of the printer: print styles cannot be
  // inspected on screen, so the page can put itself on a sheet of paper instead.
  const [preview, setPreview] = useState(
    () => new URLSearchParams(window.location.search).get("preview") === "print",
  );
  const summaries = useRef<PuzzleSummary[] | undefined>(undefined);
  const page = useSheetZoom();

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");

    void (async () => {
      try {
        let ids = published.ids;
        if (options.seed) {
          summaries.current ??= (await fetchPuzzleSummaries(controller.signal)).puzzles;
          if (controller.signal.aborted) return;
          ids = selectPrintable(summaries.current, options.size, options.count, options.seed);
        }
        const response = await fetchSolvedPuzzles(ids, controller.signal);
        if (controller.signal.aborted) return;
        // The endpoint answers in catalogue order; the sheet follows the draw, so the
        // numbering on the puzzle sheets and the answer key agree.
        const byId = new Map(response.puzzles.map((puzzle) => [puzzle.id, puzzle]));
        setPuzzles(ids.map((id) => byId.get(id)).filter((puzzle): puzzle is Puzzle => puzzle !== undefined));
      } catch (reason: unknown) {
        if (controller.signal.aborted) return;
        setError(reason instanceof Error ? reason.message : "The printable puzzles could not load.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [options.seed, options.size, options.count, published.ids]);

  function update(next: Partial<Options>): void {
    setOptions((current) => {
      // Touching any control turns the page from "the published sheet" into a draw of
      // its own, which is what the seed in the address records.
      const merged: Options = { ...current, ...next, seed: next.seed ?? current.seed ?? newPrintSeed() };
      writeOptions(merged);
      return merged;
    });
  }

  function togglePreview(): void {
    const next = !preview;
    setPreview(next);
    const url = new URL(window.location.href);
    if (next) url.searchParams.set("preview", "print");
    else url.searchParams.delete("preview");
    window.history.replaceState(null, "", url);
  }

  const perPage = printPerPage(options.size);
  const pages = useMemo(() => {
    const grouped: Puzzle[][] = [];
    for (let index = 0; index < puzzles.length; index += perPage) {
      grouped.push(puzzles.slice(index, index + perPage));
    }
    return grouped;
  }, [puzzles, perPage]);
  const sheetCount = options.seed ? printPageCount(options.size, options.count) : pages.length;

  return (
    <main
      className="prose-page printable-page"
      id="main-content"
      ref={page}
      data-print-preview={preview ? "true" : undefined}
    >
      <div className="no-print">
        <p className="eyebrow">{seoPage?.eyebrow ?? "Paper mode"}</p>
        <h1>{seoPage?.heading ?? "Printable Nonogram"}</h1>
        <p className="lede">{seoPage?.lede ?? "Choose a size and how many you want, then print the sheet and its answer key."}</p>

        <div className="print-options" role="group" aria-label="Sheet options">
          <div className="print-option">
            <span className="print-option-label" id="print-size-label">Grid</span>
            <div className="mode-group" role="group" aria-labelledby="print-size-label">
              {PRINT_SIZES.map((size) => (
                <button
                  className={options.size === size ? "mode-button is-active" : "mode-button"}
                  key={size}
                  type="button"
                  aria-pressed={options.size === size}
                  onClick={() => update({ size })}
                >
                  {size}×{size}
                </button>
              ))}
            </div>
          </div>

          <div className="print-option">
            {/* A select, not ten buttons: the number is a quantity, and a row of ten
                boxes would be wider than the two choices either side of it. */}
            <label className="print-option-label" htmlFor="print-count">Puzzles</label>
            <select
              className="print-select"
              id="print-count"
              value={options.count}
              onChange={(event) => update({ count: Number(event.target.value) })}
            >
              {printCountOptions().map((count) => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
          </div>

          <div className="print-option">
            <span className="print-option-label" id="print-answers-label">Answer key</span>
            <div className="mode-group" role="group" aria-labelledby="print-answers-label">
              <button
                className={options.answers ? "mode-button is-active" : "mode-button"}
                type="button"
                aria-pressed={options.answers}
                onClick={() => setOptions((current) => {
                  const merged = { ...current, answers: true };
                  writeOptions(merged);
                  return merged;
                })}
              >
                Include
              </button>
              <button
                className={options.answers ? "mode-button" : "mode-button is-active"}
                type="button"
                aria-pressed={!options.answers}
                onClick={() => setOptions((current) => {
                  const merged = { ...current, answers: false };
                  writeOptions(merged);
                  return merged;
                })}
              >
                Leave out
              </button>
            </div>
          </div>
        </div>

        <p className="print-summary" role="status">
          {sheetCount} puzzle {sheetCount === 1 ? "sheet" : "sheets"}
          {options.answers ? " plus an answer key" : ", no answer key"}.
        </p>

        <div className="print-actions">
          <button className="primary-button" type="button" disabled={loading || puzzles.length === 0} onClick={() => window.print()}>
            <Printer width={18} height={18} strokeWidth={2} aria-hidden="true" /> Print
          </button>
          <button className="secondary-button" type="button" onClick={() => update({ seed: newPrintSeed() })}>
            <Refresh width={18} height={18} strokeWidth={2} aria-hidden="true" /> New selection
          </button>
          <button className="secondary-button" type="button" aria-pressed={preview} onClick={togglePreview}>
            <Eye width={18} height={18} strokeWidth={2} aria-hidden="true" />
            {preview ? "Leave preview" : "Preview the paper"}
          </button>
        </div>
      </div>

      {error && <p role="alert">{error} Please refresh and try again.</p>}

      {pages.map((group, pageIndex) => (
        <section className="print-sheet" data-per-page={perPage} key={pageIndex} aria-label={`Printable puzzles, sheet ${pageIndex + 1}`}>
          <div className="print-head">
            <PrintBrand />
            <div className="print-head-body">
              <div>
                <p className="eyebrow">{pages.length > 1 ? `Sheet ${pageIndex + 1} of ${pages.length}` : "Puzzle"}</p>
                <h2>{group.length === 1 ? "Hidden Picture" : "Hidden Pictures"}</h2>
                <p>
                  Use the clues to reveal the {group.length === 1 ? "hidden picture" : "hidden pictures"}.
                  {options.answers ? " The answer key is on the last sheet." : ""}
                </p>
              </div>
              <PrintQr />
            </div>
            <p className="print-meta"><span>Name</span><span>Date</span><span>Time</span></p>
          </div>
          <div className="print-boards" data-columns={Math.min(group.length, printColumns(options.size))}>
            {group.map((puzzle, index) => (
              <figure className="print-item" key={puzzle.id}>
                <figcaption>Puzzle {pageIndex * perPage + index + 1} · {puzzle.size}×{puzzle.size}</figcaption>
                <PrintBoard puzzle={puzzle} answer={false} />
              </figure>
            ))}
          </div>
          <PrintFootnote />
        </section>
      ))}

      {options.answers && puzzles.length > 0 && (
        <section className="print-sheet answer-sheet" aria-label="Answer key">
          <div className="print-head">
            <PrintBrand />
            <div className="print-head-body">
              <div>
                <p className="eyebrow">Answer</p>
                <h2>Answer key</h2>
                <p>Keep this sheet aside until the grids are finished.</p>
              </div>
              <PrintQr />
            </div>
          </div>
          <div className="print-answers">
            {puzzles.map((puzzle, index) => (
              <figure className="print-item" key={puzzle.id}>
                <figcaption>Puzzle {index + 1} · {puzzle.emoji} {puzzle.title}</figcaption>
                <PrintBoard puzzle={puzzle} answer cellMm={answerCellMm(puzzle.size)} />
              </figure>
            ))}
          </div>
          <PrintFootnote />
        </section>
      )}

      <div className="no-print">
        <SeoContent path={path} />
      </div>
    </main>
  );
}
